/**
 * Pricing Rule (coupon / promotion) DocType-specific translations.
 * Key: `Pricing Rule:${fieldname}`
 * Value: { en: string, th: string }
 *
 * Covers the fields that render in the experience + system views so the coupon
 * page is Thai-first (no raw English labels like "Title" / "Currency").
 */
export const pricingRuleTranslations: Record<string, { en: string; th: string }> = {
  // Identity / basics
  "Pricing Rule:name": { en: "Pricing Rule ID", th: "รหัสกฎราคา" },
  "Pricing Rule:title": { en: "Title", th: "ชื่อโปรโมชัน" },
  "Pricing Rule:disable": { en: "Disable", th: "ปิดใช้งาน" },
  "Pricing Rule:apply_on": { en: "Apply On", th: "ใช้กับ" },
  "Pricing Rule:price_or_product_discount": { en: "Price or Product Discount", th: "ประเภทส่วนลด" },
  "Pricing Rule:company": { en: "Company", th: "บริษัท" },
  "Pricing Rule:selling": { en: "Selling", th: "ใช้กับการขาย" },
  "Pricing Rule:buying": { en: "Buying", th: "ใช้กับการซื้อ" },
  "Pricing Rule:currency": { en: "Currency", th: "สกุลเงิน" },

  // Apply-rule-on scope
  "Pricing Rule:warehouse": { en: "Warehouse", th: "คลังสินค้า" },
  "Pricing Rule:mixed_conditions": { en: "Mixed Conditions", th: "นับรวมทุกสินค้า" },
  "Pricing Rule:is_cumulative": { en: "Is Cumulative", th: "สะสมยอดข้ามคำสั่งซื้อ" },

  // Quantity & amount
  "Pricing Rule:min_qty": { en: "Min Qty", th: "จำนวนขั้นต่ำ" },
  "Pricing Rule:max_qty": { en: "Max Qty", th: "จำนวนสูงสุด" },
  "Pricing Rule:min_amt": { en: "Min Amount", th: "ยอดขั้นต่ำ" },
  "Pricing Rule:max_amt": { en: "Max Amount", th: "ยอดสูงสุด" },

  // Reward — price / product discount
  "Pricing Rule:rate_or_discount": { en: "Rate or Discount", th: "อัตราหรือส่วนลด" },
  "Pricing Rule:rate": { en: "Rate", th: "ราคาพิเศษ" },
  "Pricing Rule:discount_percentage": { en: "Discount Percentage", th: "ส่วนลด (%)" },
  "Pricing Rule:discount_amount": { en: "Discount Amount", th: "จำนวนส่วนลด" },
  "Pricing Rule:apply_discount_on": { en: "Apply Discount On", th: "คิดส่วนลดจาก" },
  "Pricing Rule:max_discount_amount": { en: "Max Discount Amount", th: "ส่วนลดสูงสุดต่อบิล" },
  "Pricing Rule:for_price_list": { en: "For Price List", th: "สำหรับรายการราคา" },
  "Pricing Rule:same_item": { en: "Same Item", th: "แถมสินค้าตัวเดียวกัน" },
  "Pricing Rule:free_item": { en: "Free Item", th: "สินค้าที่แถม" },
  "Pricing Rule:free_qty": { en: "Free Qty", th: "จำนวนที่แถม" },
  "Pricing Rule:free_item_rate": { en: "Free Item Rate", th: "ราคาสินค้าแถม" },
  "Pricing Rule:free_item_uom": { en: "Free Item UOM", th: "หน่วยสินค้าแถม" },
  "Pricing Rule:round_free_qty": { en: "Round Free Qty", th: "ปัดจำนวนแถม" },
  "Pricing Rule:dont_enforce_free_item_qty": { en: "Don't Enforce Free Item Qty", th: "ไม่บังคับจำนวนแถม" },
  "Pricing Rule:is_recursive": { en: "Is Recursive", th: "ให้รางวัลซ้ำเป็นชุด" },
  "Pricing Rule:recurse_for": { en: "Recurse For", th: "ทุกๆ (จำนวน)" },
  "Pricing Rule:apply_recursion_over": { en: "Apply Recursion Over", th: "เริ่มนับหลังจาก" },

  // Shipping / custom perks
  "Pricing Rule:custom_shipping_discount_type": { en: "Shipping Discount Type", th: "ประเภทส่วนลดค่าจัดส่ง" },
  "Pricing Rule:custom_shipping_discount_value": { en: "Shipping Discount Value", th: "มูลค่าส่วนลดค่าจัดส่ง" },
  "Pricing Rule:custom_is_embroidery_coupon": { en: "Free Personalization", th: "ปรับแต่ง / ปักฟรี" },

  // Party / audience
  "Pricing Rule:applicable_for": { en: "Applicable For", th: "ใช้ได้กับ" },
  "Pricing Rule:customer": { en: "Customer", th: "ลูกค้า" },
  "Pricing Rule:customer_group": { en: "Customer Group", th: "กลุ่มลูกค้า" },
  "Pricing Rule:territory": { en: "Territory", th: "เขตพื้นที่" },
  "Pricing Rule:campaign": { en: "Campaign", th: "แคมเปญ" },
  "Pricing Rule:sales_partner": { en: "Sales Partner", th: "พันธมิตรการขาย" },
  "Pricing Rule:supplier": { en: "Supplier", th: "ผู้ขาย" },
  "Pricing Rule:supplier_group": { en: "Supplier Group", th: "กลุ่มผู้ขาย" },

  // Discount on other item
  "Pricing Rule:apply_rule_on_other": { en: "Apply Rule On Other", th: "ใช้กฎกับสินค้าอื่น" },
  "Pricing Rule:other_item_code": { en: "Other Item Code", th: "รหัสสินค้าอื่น" },
  "Pricing Rule:other_item_group": { en: "Other Item Group", th: "กลุ่มสินค้าอื่น" },
  "Pricing Rule:other_brand": { en: "Other Brand", th: "แบรนด์อื่น" },

  // Margin
  "Pricing Rule:margin_type": { en: "Margin Type", th: "ประเภทมาร์จิน" },
  "Pricing Rule:margin_rate_or_amount": { en: "Margin Rate or Amount", th: "อัตรา/จำนวนมาร์จิน" },

  // Validity / stacking
  "Pricing Rule:valid_from": { en: "Valid From", th: "ใช้ได้ตั้งแต่" },
  "Pricing Rule:valid_upto": { en: "Valid Upto", th: "ใช้ได้จนถึง" },
  "Pricing Rule:has_priority": { en: "Has Priority", th: "มีลำดับความสำคัญ" },
  "Pricing Rule:priority": { en: "Priority", th: "ลำดับความสำคัญ" },
  "Pricing Rule:apply_multiple_pricing_rules": { en: "Apply Multiple Pricing Rules", th: "ใช้ร่วมกับโปรโมชันอื่นได้" },
  "Pricing Rule:threshold_percentage": { en: "Threshold Percentage", th: "เกณฑ์ขั้นต่ำ (%)" },

  // Advanced
  "Pricing Rule:apply_discount_on_rate": { en: "Apply Discount on Discounted Rate", th: "คิดส่วนลดจากราคาที่ลดแล้ว" },
  "Pricing Rule:validate_applied_rule": { en: "Validate Applied Rule", th: "ตรวจสอบกฎที่ใช้" },
  "Pricing Rule:rule_description": { en: "Rule Description", th: "คำอธิบายกฎ" },
  "Pricing Rule:apply_on_prev_doc": { en: "Apply On Previous Document", th: "ใช้กับเอกสารก่อนหน้า" },
  "Pricing Rule:condition": { en: "Condition", th: "เงื่อนไข" },

  // Coupon / storefront (custom)
  "Pricing Rule:coupon_code_based": { en: "Coupon Code Based", th: "ใช้โค้ดคูปอง" },
  "Pricing Rule:storefront_enabled": { en: "Enable for Storefront Checkout", th: "เปิดใช้บนหน้าร้าน" },
  "Pricing Rule:storefront_show_on_detail": { en: "Show on Storefront Detail", th: "แสดงบนหน้าสินค้า" },

  // Reference
  "Pricing Rule:promotional_scheme": { en: "Promotional Scheme", th: "แผนโปรโมชัน" },
  "Pricing Rule:promotional_scheme_id": { en: "Promotional Scheme ID", th: "รหัสแผนโปรโมชัน" },

  // Audit
  "Pricing Rule:owner": { en: "Created By", th: "สร้างโดย" },
  "Pricing Rule:creation": { en: "Created On", th: "วันที่สร้าง" },
  "Pricing Rule:modified_by": { en: "Modified By", th: "แก้ไขโดย" },
  "Pricing Rule:modified": { en: "Last Modified", th: "แก้ไขล่าสุด" },
};
